import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '../../../ui/core';
import { cardHeaderSx, pillButtonSx } from '../../../ui/styles';
import { useLanePolyline, useLanePolylineMutations, useToast } from '../../../hooks';
import { apiErrorMessage } from '../../../helpers/api';
import { geoJsonToPath } from '../laneGeometry';
import { flattenLiveLimits } from '../laneEligibility';
import laneMapApi from '../../../lib/api/LaneMapApi';
import {
  findRuns,
  widenRun,
  spliceSection,
  pinEndpoints,
  pathToGeoJson,
  orderWaypointsAlong,
} from './boxSelection';
import { endpointMovesFrom } from './previewShape';
import EditorMap from './EditorMap';
import ReviewPanel from './ReviewPanel';
import HistoryPanel from './HistoryPanel';

const STAGE = { SELECT: 'select', ADJUST: 'adjust', REVIEW: 'review' };

/**
 * The backend's endpoint guard is `ST_DWithin(..., 1)` on `::geography` — a
 * candidate whose terminus sits within a metre of the stored one passes
 * without any flag. Matching that number here means a save only declares an
 * endpoint move when the server would actually require the declaration.
 * If the guard's radius changes, this has to change with it.
 */
const ENDPOINT_GUARD_METERS = 1;

/**
 * Single-lane polyline editor.
 *
 * Stages are deliberately sequential — you cannot review geometry you have
 * not snapped, and you cannot snap without a section. Each stage owns one
 * question, which keeps the panel legible on a page that is otherwise mostly
 * map.
 */
const LanePolylineEditor = () => {
  const { laneId } = useParams();
  const history = useHistory();
  const { search } = useLocation();
  const { lane, isLoading, isError, isRefreshing } = useLanePolyline(laneId);
  const { snapSection, previewPolyline, commitPolyline, isSnapping, isPreviewing, isCommitting } =
    useLanePolylineMutations(laneId);
  const toast = useToast();

  const [stage, setStage] = useState(STAGE.SELECT);
  const [selection, setSelection] = useState(null);
  // The bounds the operator actually drew, kept beyond the run computation:
  // the reverse-lane re-snap re-runs THAT SAME box against the other lane's
  // geometry. Cleared wherever the selection is, so a re-snap can never be
  // run against a box from a superseded edit.
  const [selectionBox, setSelectionBox] = useState(null);
  const [runCandidates, setRunCandidates] = useState([]);
  const [selectionError, setSelectionError] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  const [candidatePath, setCandidatePath] = useState(null);
  const [preview, setPreview] = useState(null);
  const [snapError, setSnapError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [revertError, setRevertError] = useState(null);
  const [mapboxUrl, setMapboxUrl] = useState(null);
  /**
   * Where Mapbox actually put each waypoint on the last snap, keyed by the
   * waypoint object itself.
   *
   * A Map keyed on identity rather than an array indexed by position, and
   * that choice does real work. Positions shift: deleting waypoint 3 renumbers
   * everything after it, and the snapped list is in ROUTED order while
   * `waypoints` is in click order, so keeping two arrays aligned would mean
   * translating between the two orders on every edit. Get that wrong and a
   * waypoint is labelled with a road it never touched — worse than no label,
   * because it reads as fact.
   *
   * Identity makes every case fall out on its own. Moving a waypoint replaces
   * the object, so its stale label disappears. Adding one produces an object
   * with no entry, so it is unlabelled until the next snap. Deleting one
   * leaves every OTHER waypoint's label intact — which matters, because
   * spotting two bad waypoints and deleting them one at a time is the normal
   * way to use this, and it must not turn into a blind guess after the first.
   *
   * Rebuilt from scratch on each snap, so orphaned entries never accumulate.
   */
  const [snappedByWaypoint, setSnappedByWaypoint] = useState(() => new Map());
  // Where the operator has dragged the start/end terminus handle, for a
  // selection whose run reaches a lane endpoint. `null` until dragged, in
  // which case the terminus is the lane's own current one — see `onSnap`.
  // Reset alongside the rest of the selection state in `chooseRun` and
  // `onRestart` so a new selection never inherits a stale drag from a
  // previous one.
  const [startTerminus, setStartTerminus] = useState(null);
  const [endTerminus, setEndTerminus] = useState(null);
  // The reverse lane's separate re-snap: `null` until started, then
  // `{ status, error, preview, candidateGeoJson }`. Its own error field, and
  // its own slot in ReviewPanel — deliberately NOT folded into snapError
  // (ADJUST), saveError (the primary commit) or revertError (SELECT), each of
  // which renders somewhere the operator is not looking during this flow.
  const [reverseSnap, setReverseSnap] = useState(null);

  /**
   * Which async edit attempt is current.
   *
   * Every flow here is "await a request, then set state from the result", and
   * none of them could previously tell that the operator had moved on while
   * the request was in flight. Three ways that went wrong, all reachable by
   * ordinary clicking:
   *
   *  - "Start over" during a pending impact preview cleared the candidate and
   *    selection, then the old preview resolved and pushed the editor into
   *    REVIEW anyway — with no candidate, so Save serialised `null`.
   *  - Two different history rows reverted in quick succession left
   *    `candidatePath` from the SECOND and the displayed impact from
   *    whichever preview resolved LAST. Save could then commit one geometry
   *    while the operator was reading the other's station consequences.
   *  - The same, one stage earlier, between a re-snap and a restart.
   *
   * A ref, not state: it must be readable by a closure created before the
   * change, and it must never itself trigger a render. `beginEdit` bumps the
   * token and returns it; anything that resumes after an `await` checks it
   * and bails if it is no longer the current attempt. Bailing silently is
   * correct — the operator did not cancel an operation so much as start a
   * different one, and the newer flow owns the UI now.
   */
  const editSequence = useRef(0);

  const beginEdit = useCallback(() => {
    editSequence.current += 1;
    return editSequence.current;
  }, []);

  const isCurrentEdit = useCallback((token) => editSequence.current === token, []);

  // A SECOND mutations instance, bound to the reverse lane. The primary one
  // closes over `laneId`, and `previewPolyline` posts to
  // /lanes/:id/polyline/preview — so previewing the reverse candidate through
  // it would measure the other lane's stations against this lane's id.
  // Hooks are called unconditionally and only the captured id changes; these
  // are mutations, fired on demand rather than on mount, so a null id here is
  // harmless until a reverse lane exists.
  const reverseLaneId = preview?.reverseLane?.id ?? null;
  const reverseMutations = useLanePolylineMutations(reverseLaneId);

  // Same query key the lane map uses for its limits fetch, so both pages
  // share one cache entry instead of each holding its own copy.
  //
  // DELIBERATELY THE LIVE LIMITS (`flattenLiveLimits(...).values`), NOT the
  // lane map's operator-adjustable slider state. The lane map's sliders are a
  // what-if tool — an operator can widen the corridor there to explore. This
  // preview has to predict PRODUCTION, because the geometry it is about to
  // write is read live at booking time. Sourcing the thresholds from a
  // slider would let an impact diff say "no station is lost" purely because
  // someone had dragged the corridor wider.
  const { data: limitsPayload, isError: limitsFailed } = useQuery({
    queryKey: ['lane-map-limits'],
    queryFn: () => laneMapApi.fetchLimits(),
    staleTime: 5 * 60 * 1000,
  });
  // `null` until the query resolves, and `null` for good if it fails —
  // ReviewPanel gates the impact diff and Save on that, rather than
  // dereferencing thresholds that are not there.
  const { values: limits } = useMemo(() => flattenLiveLimits(limitsPayload), [limitsPayload]);

  // The reverse lane's current geometry, straight off
  // `preview.reverseLane.polylineGeoJson` via `geoJsonToPath` — never derived
  // from anything else, and never re-fetched. `[]` whenever there is no
  // reverse twin (most lanes) or no geometry on it, so `EditorMap` stays
  // inert without needing its own null check.
  const reverseLanePath = useMemo(
    () =>
      preview?.reverseLane?.polylineGeoJson
        ? geoJsonToPath(preview.reverseLane.polylineGeoJson, preview.reverseLane.id)
        : [],
    [preview],
  );

  // The reverse lane's re-snapped candidate, once `onReverseSnap` reaches
  // `status: 'ready'`. Same source discipline: read from `reverseSnap` only,
  // never recomputed.
  const reverseCandidatePath = useMemo(
    () =>
      reverseSnap?.status === 'ready' && reverseSnap.candidateGeoJson
        ? geoJsonToPath(reverseSnap.candidateGeoJson, preview?.reverseLane?.id)
        : [],
    [reverseSnap, preview],
  );

  /**
   * Where "back" and the post-save redirect go.
   *
   * The lane map is city-scoped — it renders nothing until a `cityId` is in
   * the query string — so dropping the id lands the operator on an empty map
   * immediately after being told to go and verify that the corridor moved.
   * Carried through from whatever URL this editor was opened with.
   */
  const laneMapPath = useMemo(() => {
    const cityId = new URLSearchParams(search).get('cityId');
    return cityId
      ? `/location/lane-map?cityId=${encodeURIComponent(cityId)}`
      : '/location/lane-map';
  }, [search]);

  /**
   * The working path. Read ONCE from the unsimplified endpoint — never from
   * the lane map's simplified `/lanes` payload, which would degrade the whole
   * lane on save.
   */
  const originalPath = useMemo(
    () => (lane?.polylineGeoJson ? geoJsonToPath(lane.polylineGeoJson, lane.id) : []),
    [lane],
  );

  const chooseRun = useCallback(
    (run) => {
      // `widenRun` never refuses a run any more — a run touching a lane
      // endpoint is a legitimate selection whose free end is edited via the
      // terminus handle (see `EditorMap`) rather than an anchor vertex.
      const anchors = widenRun(run, originalPath.length);

      setSelectionError(null);
      setRunCandidates([]);
      setRevertError(null);
      // A fresh selection starts with no terminus drag of its own, even if
      // the previous one had one.
      setStartTerminus(null);
      setEndTerminus(null);
      setSelection({ run, anchors });
      setStage(STAGE.ADJUST);
    },
    [originalPath.length],
  );

  const onBoxDrawn = useCallback(
    (box) => {
      // Also guarded here, not only by `canDrawBox` on the rectangle. A
      // selection made against pre-save geometry is the first step of an edit
      // that can undo the save currently being refetched, so the refusal
      // belongs where the selection is actually formed rather than resting on
      // one prop staying correct.
      if (isRefreshing) return;

      // The rectangle was cleared. Everything it produced goes with it —
      // leaving a live selection (or still-clickable run buttons) behind a
      // rectangle the operator deliberately removed is how a cleared box
      // still ends up snapped and saved. Deliberately does NOT touch the
      // candidate or stage: clearing the box during REVIEW must not silently
      // discard a snapped candidate the operator is part-way through
      // reviewing — `Start over` is the control that does that, and it says
      // so.
      if (!box) {
        setRunCandidates([]);
        setSelectionBox(null);
        setSelection(null);
        setSelectionError(null);
        return;
      }

      const runs = findRuns(originalPath, box);

      if (runs.length === 0) {
        // Clear the previous box's candidates too. Leaving them rendered puts
        // still-clickable "Section 1 / Section 2" buttons from a SUPERSEDED
        // box next to "no part of the lane is inside that box" — and clicking
        // one selects a real but unintended stretch, which then snaps and
        // saves.
        setRunCandidates([]);
        setSelectionBox(null);
        setSelectionError('No part of the lane is inside that box.');
        return;
      }

      // Retained from here on: whichever run the operator ends up on, this is
      // the box they drew, and the reverse-lane re-snap re-runs it verbatim.
      setSelectionBox(box);

      // More than one run means the lane leaves and re-enters the box — a
      // loop, a parallel leg, a lane that doubles back. Splicing across the
      // gap would delete the geometry in between, so the operator picks.
      if (runs.length > 1) {
        setSelectionError(null);
        setRunCandidates(runs);
        return;
      }

      chooseRun(runs[0]);
    },
    [originalPath, chooseRun, isRefreshing],
  );

  const onRestart = useCallback(() => {
    // Supersede anything in flight. Without this, a snap or preview started
    // before the operator pressed Start over would resolve afterwards and
    // push the editor into REVIEW with no candidate behind it.
    beginEdit();
    setSelection(null);
    setSelectionBox(null);
    setReverseSnap(null);
    setRunCandidates([]);
    setSelectionError(null);
    setWaypoints([]);
    setSnappedByWaypoint(new Map());
    setCandidatePath(null);
    setPreview(null);
    setSnapError(null);
    setSaveError(null);
    setRevertError(null);
    setMapboxUrl(null);
    setStartTerminus(null);
    setEndTerminus(null);
    setStage(STAGE.SELECT);
  }, [beginEdit]);

  /**
   * Throw away a snapped candidate that no longer describes the inputs.
   *
   * Since a snap stops in ADJUST, the waypoints and terminus handles stay
   * editable with a candidate already on the map — and nothing tied the two
   * together. An operator could delete the waypoint that grabbed the wrong
   * road, press Review impact WITHOUT snapping again, and save a route that
   * still ran through the point they had just removed. The impact preview
   * would have been perfectly accurate about the wrong geometry.
   *
   * So any change to the snap's inputs retires its output. The snapped
   * waypoint labels deliberately survive: they are keyed by waypoint
   * identity, so the ones still on the map remain true, and they are what the
   * operator is reading while deciding what else to delete.
   */
  const invalidateCandidate = useCallback(() => {
    // Supersede anything in flight as well as clearing what already landed.
    // Clearing alone was not enough: a snap still running against the OLD
    // inputs would resolve afterwards and restore `candidatePath` from them,
    // handing the operator a reviewable, saveable route built from a waypoint
    // they had just deleted. A late preview would do the mirror image — enter
    // REVIEW after the candidate was cleared, where Save serialises null.
    beginEdit();
    setCandidatePath(null);
    setMapboxUrl(null);
  }, [beginEdit]);

  // Dragging a terminus changes what the snap would route, exactly as moving
  // a waypoint does, so it retires the candidate the same way.
  const onMoveTerminus = useCallback(
    (which, point) => {
      if (which === 'start') {
        setStartTerminus(point);
      } else {
        setEndTerminus(point);
      }
      invalidateCandidate();
    },
    [invalidateCandidate],
  );

  const onAddWaypoint = useCallback(
    (point) => {
      setWaypoints((current) => [...current, point]);
      invalidateCandidate();
    },
    [invalidateCandidate],
  );

  const onMoveWaypoint = useCallback(
    (index, point) => {
      setWaypoints((current) => current.map((wp, i) => (i === index ? point : wp)));
      invalidateCandidate();
    },
    [invalidateCandidate],
  );

  const onRemoveWaypoint = useCallback(
    (index) => {
      setWaypoints((current) => current.filter((_, i) => i !== index));
      invalidateCandidate();
    },
    [invalidateCandidate],
  );

  /**
   * What the snap will route between, derived once.
   *
   * A free end has no anchor vertex to route from, so its origin is the
   * terminus handle's position instead — the lane's own current terminus
   * until the operator drags it (see `EditorMap`'s `startTerminus`/
   * `endTerminus`). Where a real anchor exists it is still used.
   *
   * `startDragged`/`endDragged` is whether the operator actually dragged that
   * handle — NOT whether the selection merely permits moving it
   * (`movesStart`/`movesEnd`, which is what `allowStartMove`/`allowEndMove`
   * on the commit is about). Only an end the operator actually moved is
   * exempt from `pinEndpoints`; an untouched handle means the edit behaves
   * exactly as it did before endpoint editing existed.
   */
  const snapAnchors = useMemo(() => {
    if (!selection || originalPath.length === 0) return null;

    const { anchorStartIndex, anchorEndIndex, movesStart, movesEnd } = selection.anchors;

    return {
      anchorStart: movesStart
        ? startTerminus ?? originalPath[0]
        : originalPath[anchorStartIndex],
      anchorEnd: movesEnd
        ? endTerminus ?? originalPath[originalPath.length - 1]
        : originalPath[anchorEndIndex],
      startDragged: movesStart && startTerminus !== null,
      endDragged: movesEnd && endTerminus !== null,
    };
  }, [selection, originalPath, startTerminus, endTerminus]);

  /**
   * The lane's own geometry for the stretch being edited, anchors included.
   *
   * The reference for ordering waypoints. It has to be the real shape of the
   * corridor rather than the straight line between the anchors: on a stretch
   * that doubles back, waypoints placed in genuine road order project onto
   * that straight line in the wrong order.
   *
   * Falls back to just the two anchors when the run holds no vertices — the
   * crossed-segment case — which is exactly the straight line, and correct
   * there, because a single segment cannot double back on itself.
   */
  const selectedSectionPath = useMemo(() => {
    if (!snapAnchors || !selection) return [];

    const { anchorStartIndex, anchorEndIndex } = selection.anchors;
    const interior =
      anchorStartIndex === null || anchorEndIndex === null
        ? []
        : originalPath.slice(anchorStartIndex + 1, anchorEndIndex);

    return [snapAnchors.anchorStart, ...interior, snapAnchors.anchorEnd].filter(
      Boolean,
    );
  }, [snapAnchors, selection, originalPath]);

  /**
   * The waypoints in the order the route will VISIT them.
   *
   * Derived here, once, and used for BOTH the snap request and the numbers on
   * the map markers. Those two must not be computed separately: a map showing
   * one order while the request used another would be worse than no numbers
   * at all.
   */
  const routedWaypoints = useMemo(
    () => orderWaypointsAlong(selectedSectionPath, waypoints),
    [selectedSectionPath, waypoints],
  );

  /**
   * One row per waypoint that has snapped feedback, in ROUTED order.
   *
   * Routed order, because that is the order the numbers on the map markers
   * use and the order the route actually visits them — a list numbered
   * differently from the map would be worse than no list.
   *
   * `clickIndex` is carried alongside because deletion is indexed against
   * `waypoints`, which is in CLICK order. That translation happens here,
   * once, rather than at the click handler where an off-by-one would delete
   * a waypoint the operator was not pointing at.
   */
  const snappedWaypointRows = useMemo(
    () =>
      routedWaypoints
        .map((waypoint, position) => {
          const info = snappedByWaypoint.get(waypoint);
          if (!info) return null;

          const clickIndex = waypoints.indexOf(waypoint);
          if (clickIndex === -1) return null;

          // Both of these are nullable on purpose — see `SnappedWaypoint` on
          // the server. `distanceMeters` is null when the displacement could
          // not be determined at all, and it must NOT collapse to 0 here:
          // zero renders as "on your click", the most reassuring thing this
          // list can say, on a waypoint that may have been dragged onto
          // entirely the wrong road.
          const moved =
            typeof info.distanceMeters === 'number'
              ? Math.round(info.distanceMeters)
              : null;

          return {
            key: `${clickIndex}-${position}`,
            position: position + 1,
            clickIndex,
            name: info.name,
            // `[lng, lat]` on the wire (GeoJSON order), `{lat, lng}` for the
            // map. Transposed here, once, at the boundary. Null when the
            // server could not say where it landed — drawing a marker at a
            // defaulted `[0, 0]` would put it in the Gulf of Guinea.
            snappedPoint: info.location
              ? { lat: info.location[1], lng: info.location[0] }
              : null,
            // Rounded to whole metres: the operator is deciding "did this
            // land on the road I meant", and sub-metre precision is noise.
            distanceLabel:
              moved === null
                ? 'snap distance unknown'
                : moved <= 1
                  ? 'on your click'
                  : `moved ${moved} m`,
          };
        })
        .filter(Boolean),
    [routedWaypoints, snappedByWaypoint, waypoints],
  );

  const onSnap = useCallback(async () => {
    if (!selection || !snapAnchors) return;

    const token = beginEdit();
    setSnapError(null);

    const { anchorStart, anchorEnd, startDragged, endDragged } = snapAnchors;
    const { anchorStartIndex, anchorEndIndex } = selection.anchors;

    // Routed in corridor order, not click order. Mapbox visits waypoints in
    // the order given and never reorders them, so a waypoint added while
    // refining a stretch already passed — which appends to the end of the
    // list — sent the route back up the corridor and down again. See
    // `orderWaypointsAlong`.
    const snapped = await snapSection({
      anchorStart,
      anchorEnd,
      waypoints: routedWaypoints,
    });

    if (!isCurrentEdit(token)) return;

    if (!snapped.success) {
      // Stay in ADJUST. The fix for a rejected snap is almost always moving a
      // waypoint, and dropping the selection would make the operator redraw
      // the box for nothing.
      setSnapError(apiErrorMessage(snapped.error) || 'Could not snap that section.');
      return;
    }

    const section = snapped.data.coordinates.map(([lng, lat]) => ({ lat, lng }));
    // Pin the lane's own endpoints back on after the splice, EXCEPT an end
    // the operator actually dragged — pinning that one back would silently
    // discard the drag. `widenRun` permits a start anchor of index 0 (and,
    // symmetrically, an end anchor at the last index), and `spliceSection`
    // consumes both anchors — so an edit near a STAYING-PUT lane start or end
    // would otherwise replace the lane's true endpoint with the ROAD-SNAPPED
    // coordinate the routing service returned. The backend enforces endpoint
    // equality within 1 m (`ST_StartPoint`/`ST_EndPoint`), so nothing
    // corrupts; but a snapped endpoint can drift further than 1 m, and the
    // operator then gets a rejection that reads as "the lane changed since
    // you loaded it", which is both wrong and unactionable. Hold the
    // invariant structurally here rather than trusting the router to return a
    // coordinate it was never asked to preserve.
    const nextPath = pinEndpoints(
      spliceSection(originalPath, anchorStartIndex, anchorEndIndex, section),
      originalPath,
      { startDragged, endDragged },
    );

    setCandidatePath(nextPath);
    setMapboxUrl(snapped.data.mapboxUrl);
    // Where Mapbox actually put each waypoint. A click on a map is not a
    // point on a road, and near a highway the nearest centreline is regularly
    // NOT the highway — so this is what lets the operator see that waypoint 3
    // landed on a hospital access road rather than infer it from a detour.
    //
    // Zipped against `routedWaypoints`, NOT `waypoints`: the server answers in
    // the order it was asked, which is corridor order. Zipping against click
    // order would attach each road name to the wrong point.
    const snappedInfo = snapped.data.snappedWaypoints ?? [];
    setSnappedByWaypoint(
      new Map(
        snappedInfo
          .map((info, index) => [routedWaypoints[index], info])
          .filter(([waypoint]) => waypoint !== undefined),
      ),
    );

    // DELIBERATELY STOPS HERE, in ADJUST, rather than previewing and
    // advancing to REVIEW.
    //
    // Snapping is iterative: the operator looks at the returned line, sees a
    // waypoint that grabbed the wrong road, deletes it, and snaps again. Auto
    // -advancing made every one of those iterations a round trip out of
    // REVIEW and back, and spent an impact preview on geometry the operator
    // was about to discard. The preview is only needed to make a SAVE safe,
    // and it is still mandatory before one — it just happens when the
    // operator says the geometry is right.
  }, [
    selection,
    snapAnchors,
    routedWaypoints,
    originalPath,
    snapSection,
    beginEdit,
    isCurrentEdit,
  ]);

  /**
   * Take the snapped candidate to the impact preview.
   *
   * The gate between "the line looks right" and "here is what it does to
   * bookability". Nothing may reach REVIEW without a successful preview —
   * ReviewPanel is where Save lives, and a save without a measured impact is
   * the thing this whole tool exists to prevent.
   */
  const onReviewImpact = useCallback(async () => {
    if (!candidatePath) return;

    const token = beginEdit();
    setSnapError(null);

    const previewed = await previewPolyline(pathToGeoJson(candidatePath));
    if (!isCurrentEdit(token)) return;

    if (previewed.success) {
      setPreview(previewed.data);
      setStage(STAGE.REVIEW);
    } else {
      // Stays in ADJUST, where the operator can keep working on the geometry.
      setSnapError(
        apiErrorMessage(previewed.error) ||
          'Could not measure the impact of that candidate. Not safe to save without it.',
      );
    }
  }, [candidatePath, previewPolyline, beginEdit, isCurrentEdit]);

  /**
   * Re-snap the reverse lane — the same pipeline the primary lane just ran,
   * against the OTHER lane's own geometry.
   *
   * The same box and the same waypoints are correct here. Both lanes serve the
   * same corridor, and the waypoints are absolute geographic points describing
   * where the road actually runs — not offsets into one lane's vertex array.
   * What is NOT shared is the geometry: nothing from `originalPath` or
   * `candidatePath` reaches the reverse lane. It is snapped, spliced and
   * previewed on its own, and the operator accepts it on its own impact.
   *
   * Every failure below leaves `status: 'failed'` with an operator-facing
   * message. Nothing here may fail quietly: a silent failure would leave the
   * panel looking as if the other lane were handled.
   */
  const onReverseSnap = useCallback(async () => {
    const reverseLane = preview?.reverseLane;
    if (!reverseLane) return;

    // The same supersede-token every other async flow here uses, and it was
    // the one flow missing it. The interleaving that mattered: start a
    // re-snap, press Cancel while it runs (which resets the stage and clears
    // `reverseSnap`), and the in-flight snap then resolved and revived
    // `reverseSnap` as `ready` — carrying a candidate built from the box that
    // Cancel had just thrown away. Reach REVIEW again on a fresh edit of the
    // same lane and that stale candidate is presented alongside the new
    // primary one. Because it is the same reverse lane, the city names match,
    // so nothing looks wrong; accepting it would write reverse-lane geometry
    // derived from a box that produced none of the current candidate.
    const token = beginEdit();

    const label = `${reverseLane.originCityName} → ${reverseLane.destinationCityName}`;
    const fail = (error) => {
      if (!isCurrentEdit(token)) return;
      setReverseSnap({ status: 'failed', error, preview: null, candidateGeoJson: null });
    };

    setReverseSnap({ status: 'running', error: null, preview: null, candidateGeoJson: null });

    // Only reachable from a reverted candidate, which was never boxed.
    if (!selectionBox) {
      fail(
        `This candidate came from a history revert, so there is no drawn box to ` +
          `re-run against ${label}. Correct that lane in its own editor session.`,
      );
      return;
    }

    const reversePath = geoJsonToPath(reverseLane.polylineGeoJson, reverseLane.id);
    const runs = findRuns(reversePath, selectionBox);

    if (runs.length === 0) {
      fail(
        `No part of ${label} is inside the box you drew, so there is nothing to ` +
          `re-snap on that lane. Correct it in its own editor session.`,
      );
      return;
    }

    // Deliberately a refusal rather than a second run-picker. Picking one
    // silently could splice across the gap between the runs and delete the
    // geometry in between — on a lane the operator is not even looking at.
    if (runs.length > 1) {
      fail(
        `${label} enters that box ${runs.length} times, so which stretch you ` +
          `mean is ambiguous. Correct that lane in its own editor session.`,
      );
      return;
    }

    const anchors = widenRun(runs[0], reversePath.length);

    // The reverse lane's re-snap has no endpoint-intent channel — there is no
    // `allowStartMove`/`allowEndMove` for it to declare the way the primary
    // lane's commit does (see `onSave`), because there is nowhere for the
    // operator to set a terminus handle for a lane they never selected on.
    // Refuse plainly here rather than sending a write the backend's ordinary
    // section-edit guard would reject as a confusing "changed since you
    // loaded it" conflict. This is a real product gap, not something to work
    // around by inventing a DTO field for it.
    if (anchors.movesStart || anchors.movesEnd) {
      fail(
        `That box reaches the start or end of ${label}. This re-snap has no ` +
          `way to declare an endpoint move for that lane, so correct it in ` +
          `its own editor session.`,
      );
      return;
    }

    const { anchorStartIndex, anchorEndIndex } = anchors;

    // The snap endpoint ignores its lane id — the backend's `snapSection` only
    // routes the given anchors and waypoints through Mapbox. Called on the
    // reverse instance anyway, so the call site reads consistently and stays
    // correct if the backend ever starts using that id.
    // Ordered along the REVERSE lane's own axis, which runs the other way.
    // The waypoints are absolute geographic points, so they are shared — but
    // the sequence to visit them in is not: routing this lane through them in
    // the primary lane's order would weave back and forth along it. Deriving
    // the order from THAT LANE'S OWN geometry is what makes the shared points
    // correct for both directions.
    const reverseAnchorStart = reversePath[anchorStartIndex];
    const reverseAnchorEnd = reversePath[anchorEndIndex];

    const snapped = await reverseMutations.snapSection({
      anchorStart: reverseAnchorStart,
      anchorEnd: reverseAnchorEnd,
      waypoints: orderWaypointsAlong(
        reversePath.slice(anchorStartIndex, anchorEndIndex + 1),
        waypoints,
      ),
    });

    if (!isCurrentEdit(token)) return;

    if (!snapped.success) {
      fail(apiErrorMessage(snapped.error) || `Could not snap that section on ${label}.`);
      return;
    }

    const section = snapped.data.coordinates.map(([lng, lat]) => ({ lat, lng }));
    // Same endpoint pin as the primary lane, for the same reason: the reverse
    // lane's own true endpoints must survive an edit that reached them.
    const nextPath = pinEndpoints(
      spliceSection(reversePath, anchorStartIndex, anchorEndIndex, section),
      reversePath,
    );
    const candidateGeoJson = pathToGeoJson(nextPath);

    // Against the REVERSE lane's id — its stations, its corridor.
    const previewed = await reverseMutations.previewPolyline(candidateGeoJson);
    if (!isCurrentEdit(token)) return;

    if (!previewed.success) {
      // No preview, no acceptance. The whole point is that the operator
      // accepts the other lane on its own evidence.
      fail(
        apiErrorMessage(previewed.error) ||
          `Re-snapped ${label}, but its impact preview failed. It cannot be included without one.`,
      );
      return;
    }

    setReverseSnap({
      status: 'ready',
      error: null,
      preview: previewed.data,
      candidateGeoJson,
    });
  }, [preview, selectionBox, waypoints, reverseMutations, beginEdit, isCurrentEdit]);

  const onReverseDiscard = useCallback(() => setReverseSnap(null), []);

  /**
   * Which endpoint moves this save is permitted to make.
   *
   * Two different sources, because there are two ways to reach REVIEW and
   * they establish intent differently.
   *
   * **A snapped edit** takes them from selection membership — "this edit is
   * permitted to move that terminus". Permission, not measurement: whether
   * the operator actually dragged the handle is measured server-side and
   * recorded in the audit trail, so the flag does not need to double as a
   * measurement.
   *
   * **A revert** has no selection, and an earlier version defaulted both to
   * false on the reasoning that "a revert never claims permission to move an
   * endpoint". That was wrong, and it made a whole class of history
   * unrevertable: reverting an edit that moved a terminus restores the old
   * terminus, so the revert moves one too — and with both flags false the
   * backend's endpoint guard rejected it, after its impact preview had
   * already succeeded. The operator saw a valid preview and an unexplainable
   * conflict.
   *
   * So a revert derives them from what it actually does, using the server's
   * OWN measurement of this candidate against the stored geometry rather than
   * a second client-side comparison that could disagree with the guard it has
   * to satisfy.
   */
  const endpointMovePermissions = useMemo(() => {
    if (selection) {
      return {
        allowStartMove: !!selection.anchors?.movesStart,
        allowEndMove: !!selection.anchors?.movesEnd,
      };
    }

    // Through `endpointMovesFrom`, never off `preview` directly — ReviewPanel
    // reads the same two numbers, and the two files previously disagreed
    // about where they live.
    const { startMovedMeters, endMovedMeters } = endpointMovesFrom(preview);

    return {
      allowStartMove: startMovedMeters > ENDPOINT_GUARD_METERS,
      allowEndMove: endMovedMeters > ENDPOINT_GUARD_METERS,
    };
  }, [selection, preview]);

  const onSave = useCallback(
    async ({ reason, applyToReverse, reverseCandidateGeoJson }) => {
      setSaveError(null);

      const result = await commitPolyline({
        candidateGeoJson: pathToGeoJson(candidatePath),
        expectedUpdatedAt: lane.updatedAt,
        reason,
        applyToReverse,
        reverseCandidateGeoJson,
        // The reverse lane's OWN concurrency token, from the preview the
        // operator actually reviewed. Sent whenever a reverse lane exists,
        // not only when `applyToReverse` is set: an identical pair is
        // mirrored server-side without this client asking, so keying it off
        // that flag would leave the mirror path unprotected.
        //
        // Without it the server had to re-read that lane's updatedAt at
        // commit time, which always matched and so guarded nothing — an
        // admin who edited the reverse lane while this operator was
        // reviewing would be overwritten silently, against a station diff
        // computed from geometry that no longer existed.
        expectedReverseUpdatedAt: preview?.reverseLane?.updatedAt,
        editContext: { waypoints, mapboxUrl, run: selection?.run },
        ...endpointMovePermissions,
      });

      if (!result.success) {
        // Rendered inside the review panel, next to Save — NOT reused from
        // snapError, which renders in the ADJUST stage the operator has
        // already left.
        setSaveError(apiErrorMessage(result.error) || 'Could not save.');
        return;
      }

      // Defensive on the count, not on the success: the write has already
      // happened by here, so a response missing `updatedLaneIds` must not
      // throw on the way to reporting it — that would leave the operator
      // looking at an error over a save that landed.
      const updatedCount = result.data?.updatedLaneIds?.length;
      toast.success(
        updatedCount ? `Saved. ${updatedCount} lane(s) updated.` : 'Saved.',
      );

      // Stay on the lane's page rather than redirecting to the lane map.
      // The save has just produced a history entry describing what was
      // changed and why, and the commit invalidates that query — so landing
      // back in SELECT shows the operator their own edit, on the page they
      // made it from. Redirecting away sent them to the one view that cannot
      // show it, which is how a successful save came to look like nothing had
      // happened. "Back to lane map" is still there for anyone who wants it.
      onRestart();
    },
    [
      candidatePath,
      lane,
      // Carries the reverse lane's concurrency token. Omitting it here would
      // pin this callback to the token from the first preview of the
      // session, so a re-preview after the reverse lane changed would still
      // save against the stale one — the exact failure the token prevents.
      preview,
      waypoints,
      mapboxUrl,
      selection,
      endpointMovePermissions,
      commitPolyline,
      onRestart,
      toast,
    ],
  );

  /**
   * Revert does NOT write. It loads the chosen history row's previous
   * geometry as the candidate and runs it through the same impact preview
   * and REVIEW stage as any other edit — a revert is still a geometry
   * change with the same blast radius, since the stations around the lane
   * may have changed since the original edit.
   */
  const onRevert = useCallback(
    async (previousGeoJson) => {
      // Supersedes any earlier revert still previewing. Without it, clicking
      // two history rows in quick succession left `candidatePath` from the
      // second and the displayed impact from whichever preview resolved last
      // — so Save could commit one geometry while the operator was reading
      // the other's station consequences.
      const token = beginEdit();
      setRevertError(null);
      setSaveError(null);
      setSnapError(null);
      // A revert reaches REVIEW without the operator having drawn anything, so
      // any box left over from an earlier attempt must not survive into it —
      // re-snapping the reverse lane against a box that produced none of this
      // candidate would be meaningless.
      setSelectionBox(null);
      setReverseSnap(null);
      // A revert has nothing to do with whatever was selected before it. Left
      // set, `selection` would supply `onSave`'s endpoint-move flags from a
      // stretch of lane this candidate was not derived from — so reverting
      // after a terminal selection would claim permission to move an endpoint
      // that this revert may not move at all. The flags for a revert come
      // from what the revert actually does; see `onSave`.
      setSelection(null);
      setRunCandidates([]);
      setWaypoints([]);
      // A reverted candidate came from history, not from a snap — there are
      // no waypoints behind it and so no snapped positions to describe.
      setSnappedByWaypoint(new Map());

      const nextPath = geoJsonToPath(previousGeoJson, lane?.id);
      setCandidatePath(nextPath);
      setMapboxUrl(null);

      const previewed = await previewPolyline(pathToGeoJson(nextPath));
      if (!isCurrentEdit(token)) return;

      if (previewed.success) {
        setRevertError(null);
        setPreview(previewed.data);
        setStage(STAGE.REVIEW);
      } else {
        // Stay in SELECT. Rendered next to HistoryPanel — NOT saveError,
        // which only ReviewPanel renders, and the editor never reaches
        // REVIEW on this path, so that message would have no visible slot.
        setRevertError(
          apiErrorMessage(previewed.error) ||
            'Could not preview that revert. Not safe to save without it.',
        );
      }
    },
    [lane, previewPolyline, beginEdit, isCurrentEdit],
  );

  if (isLoading) {
    return <Typography sx={{ p: 4 }}>Loading lane…</Typography>;
  }

  if (isError || !lane) {
    return (
      <Alert severity="error" sx={{ m: 4 }}>
        Could not load this lane&apos;s geometry.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" alignItems="center" sx={{ ...cardHeaderSx, px: 4, py: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">
            {lane.originCityName} → {lane.destinationCityName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {lane.vertexCount} vertices · {Math.round(lane.lengthMeters / 1000)} km
          </Typography>
        </Box>
        <Button sx={pillButtonSx} onClick={() => history.push(laneMapPath)}>
          Back to lane map
        </Button>
      </Stack>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EditorMap
            lane={lane}
            path={originalPath}
            selection={selection}
            runCandidates={runCandidates}
            onBoxDrawn={onBoxDrawn}
            waypoints={waypoints}
            // The same array the snap request uses, so the numbers on the map
            // can never disagree with the order actually routed.
            routedWaypoints={routedWaypoints}
            // Where each waypoint actually ended up. Drawn as well as listed:
            // the list says "moved 184 m", the map says onto WHAT — and a
            // waypoint sitting visibly on a frontage road beside the highway
            // explains the detour in a way a number cannot.
            snappedWaypointRows={snappedWaypointRows}
            onAddWaypoint={onAddWaypoint}
            onMoveWaypoint={onMoveWaypoint}
            onRemoveWaypoint={onRemoveWaypoint}
            startTerminus={startTerminus}
            endTerminus={endTerminus}
            onMoveTerminus={onMoveTerminus}
            candidatePath={candidatePath}
            // Stage, not `!!selection`: a revert reaches REVIEW with no
            // selection at all, and a rectangle still live there would let a
            // stray drag throw away the reverted candidate under review.
            //
            // And never while the lane's geometry is being refreshed — see
            // the note beside the panel's reloading state. A box drawn
            // against pre-save geometry is the first step of an edit that can
            // undo the save that triggered the refresh.
            canDrawBox={stage === STAGE.SELECT && !isRefreshing}
            stage={stage}
            reverseLanePath={reverseLanePath}
            reverseCandidatePath={reverseCandidatePath}
          />
        </Box>

        <Box sx={{ width: 380, borderLeft: 1, borderColor: 'divider', p: 2, overflowY: 'auto' }}>
          {selectionError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {selectionError}
            </Alert>
          )}

          {runCandidates.length > 1 && (
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                The lane enters that box {runCandidates.length} times
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pick the stretch you meant.
              </Typography>
              {runCandidates.map((run, index) => (
                <Button
                  key={`${run.startIndex}-${run.endIndex}`}
                  sx={pillButtonSx}
                  onClick={() => chooseRun(run)}
                >
                  {/* A run over a crossed segment holds no vertices — saying
                      "0 vertices" would read as an empty, pointless choice
                      rather than a legitimate one. */}
                  {run.endIndex < run.startIndex
                    ? `Section ${index + 1} · between two vertices`
                    : `Section ${index + 1} · ${run.endIndex - run.startIndex + 1} vertices`}
                </Button>
              ))}
            </Stack>
          )}

          {/* Editing is held while the lane's geometry is refetching, which is
              the window right after a save invalidates it. During that window
              `lane` still holds the PRE-SAVE geometry and, worse, the pre-save
              `updatedAt`. An operator who starts a second edit inside it would
              build a candidate from the old path and then send it with the
              refreshed token — which the backend accepts, silently undoing
              part of the save they just made everywhere outside the newly
              selected stretch.

              This is the exact hazard `useLanePolyline`'s `staleTime:
              Infinity` was chosen to avoid; staying on the page after a save,
              rather than redirecting away, is what reintroduced it. */}
          {stage === STAGE.SELECT && isRefreshing && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reloading this lane&apos;s geometry after the save…
            </Typography>
          )}

          {stage === STAGE.SELECT && !isRefreshing && runCandidates.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Drag the rectangle over the stretch you want to correct, or
              resize it by its corner handles. The rest of the lane stays
              frozen.
            </Typography>
          )}

          {revertError && stage === STAGE.SELECT && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {revertError}
            </Alert>
          )}

          {stage === STAGE.ADJUST && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Adjust</Typography>
              <Typography variant="caption" color="text.secondary">
                Drop waypoints where the road actually runs, then snap. Snap as
                many times as you like — the impact is only measured when you
                ask for it.
              </Typography>
              {snapError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {snapError}
                </Alert>
              )}

              {snappedWaypointRows.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Typography variant="subtitle2">Where each waypoint landed</Typography>
                  <Typography variant="caption" color="text.secondary">
                    A click is not a point on a road. Mapbox moves each one to
                    the nearest centreline, which near a highway is often a
                    frontage road or a ramp — and the route then goes that way.
                    Delete any that grabbed the wrong road and snap again.
                  </Typography>
                  {snappedWaypointRows.map((row) => (
                    <Stack
                      key={row.key}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mt: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ flex: 1 }}>
                        {`${row.position}. ${row.name || 'Unnamed road'} · ${row.distanceLabel}`}
                      </Typography>
                      <Button
                        size="small"
                        sx={pillButtonSx}
                        onClick={() => onRemoveWaypoint(row.clickIndex)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}

              <Button sx={pillButtonSx} onClick={onSnap} disabled={isSnapping}>
                {candidatePath ? 'Snap again' : 'Snap section'}
              </Button>
              {/* Only once there is geometry to measure. The preview is what
                  makes a save safe, so REVIEW stays unreachable without one —
                  this button is the gate, not a shortcut past it. */}
              {candidatePath && (
                <Button
                  sx={pillButtonSx}
                  onClick={onReviewImpact}
                  disabled={isPreviewing || isSnapping}
                >
                  Review impact
                </Button>
              )}
              <Button sx={pillButtonSx} onClick={onRestart}>
                Start over
              </Button>
            </Stack>
          )}

          {stage === STAGE.REVIEW && preview && (
            <ReviewPanel
              preview={preview}
              lane={lane}
              limits={limits}
              limitsFailed={limitsFailed}
              onSave={onSave}
              onCancel={onRestart}
              isSaving={isCommitting}
              saveError={saveError}
              reverseSnap={reverseSnap}
              onReverseSnap={onReverseSnap}
              onReverseDiscard={onReverseDiscard}
            />
          )}

          {/* Always on the page, at every stage, rather than only in SELECT.
              This is a lane's page, not an editing mode: "what was changed
              here and why" is a question an operator has whether or not they
              are mid-edit, and the previous gating meant a save redirected
              them away from the only place that could answer it.

              Reverting stays confined to SELECT. A revert replaces the
              candidate outright, so offering it while one is being adjusted
              or reviewed would discard that work with one click — and in
              REVIEW it would swap the geometry out from under the very
              station diff the operator is reading. Visible everywhere,
              actionable in one place. */}
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <HistoryPanel
              laneId={laneId}
              onRevert={onRevert}
              canRevert={stage === STAGE.SELECT && !isRefreshing}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LanePolylineEditor;
